import { useLocation, useNavigate } from "react-router-dom";
import ButtonComponent from "../shared/button/button.component";
import { useEffect, useMemo, useState, useRef } from "react";
import { ProfileModel } from '../../models/profile.model';
import { UserService } from "../../services/UserService";
import { SecureDocsService } from "../../services/SecureDocsService";
import { Document, Page } from 'react-pdf';
import InteractiveSigningField from "./interactive-signing-field.component";
import { AppRoutes } from "../routes/route.path";
import { useSelector } from "react-redux";
import * as selector from "../../selectors/selectors";
import io from "socket.io-client";
import { ChevronLeftIcon, ChevronRightIcon, FalconLogo } from "../../icon";
import setToast from "../../helpers/toaster.helper";
import { ToasterType } from "../shared/toaster/toaster.model";
import Config from "../../config";
import { appNote, buttonLabels, ToasterTexts } from "../shared/constants";

interface DocumentField {
    id: string;
    type: string;
    page: number;
    position: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    assignedTo: {
        recipient_id: string;
        email: string;
    };
    completed: boolean;
    value?: string;
    completedAt?: string;
}

interface UserInput {
    fieldId: string;
    value: string;
    type: string;
}

const uint8ArrayToBase64 = (uint8Array: Uint8Array): string => {
    let binaryString = '';
    const len = uint8Array.byteLength;
    for (let i = 0; i < len; i++) {
        binaryString += String.fromCharCode(uint8Array[i]);
    }
    return btoa(binaryString);
};

const base64ToUint8Array = (base64: string): Uint8Array => {
    try {
        const cleanBase64 = base64.replace(/\s/g, '');
        if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleanBase64)) {
            throw new Error('Invalid base64 string');
        }

        const binaryString = atob(cleanBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return bytes;
    } catch (error) {
        throw new Error('Failed to decode base64 PDF data');
    }
};

const decodeJWT = (token: string) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        return null;
    }
};

const EndUserSigningComponent: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const response = location.state?.response;
    const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
    const [originalPdfBase64, setOriginalPdfBase64] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [documentFields, setDocumentFields] = useState<DocumentField[]>([]);
    const [userInputs, setUserInputs] = useState<Map<string, UserInput>>(new Map());
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeSignatureFieldId, setActiveSignatureFieldId] = useState<string | null>(null);
    const [pdfWidth, setPdfWidth] = useState<number>(800);
    const [actualPdfSize, setActualPdfSize] = useState<{ width: number; height: number } | null>(null);
    const pdfContainerRef = useRef<HTMLDivElement>(null);
    const [showRefreshModal, setShowRefreshModal] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState<string>('');
    const socketRef = useRef<any>(null);
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const fileData = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData]);
    const fileId = response?.data?.[0]?.file_id;
    const fileName = response?.data?.[0]?.file_name;
    const token = response?.token;
    const pdfBase64 = response?.data?.[0]?.value;
    const configurations = response?.data?.[0]?.configurations;
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [masterSignature, setMasterSignature] = useState<string | null>(null);
    const [showAgreementModal, setShowAgreementModal] = useState(true);
    const [agreementAccepted, setAgreementAccepted] = useState(false);

    const userFields = useMemo(() => {
        if (!token) return [];
        const currentUserEmail = decodeJWT(token)?.UserId;
        return documentFields.filter(field => field.assignedTo.email === currentUserEmail);
    }, [documentFields, token]);

    const completedUserFields = useMemo(() => {
        return userFields.filter(field =>
            userInputs.has(field.id) || field.completed
        );
    }, [userFields, userInputs]);

    const remainingFields = useMemo(() => {
        return userFields.length - completedUserFields.length;
    }, [userFields.length, completedUserFields.length]);

    const userFieldsOnCurrentPage = useMemo(() => {
        return userFields.filter(field => field.page === currentPage);
    }, [userFields, currentPage]);

    const pagesWithUserFields = useMemo(() => {
        const pages = Array.from(new Set(
            userFields.map(field => field.page)
        )).sort((a, b) => a - b);
        return pages;
    }, [userFields]);

    const calculatePdfWidth = () => {
        if (pdfContainerRef.current) {
            const containerWidth = pdfContainerRef.current.offsetWidth;
            const maxWidth = IsMobileDevice ? containerWidth - 40 : 800;
            setPdfWidth(Math.min(maxWidth, 800));
        }
    };

    useEffect(() => {
        calculatePdfWidth();
        const handleResize = () => calculatePdfWidth();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [IsMobileDevice]);

    useEffect(() => {
        if (!fileId || !token) return;

        const currentUserEmail = decodeJWT(token)?.UserId;
        if (!currentUserEmail) {
            return;
        }

        const socket = io(`${Config.API_SECOND_URL}`, {
            transports: ["websocket", "polling"],
            query: {
                room: fileId
            },
        });

        socketRef.current = socket;

        socket.on("chat message", (message) => {
            try {
                let messageData;

                if (typeof message === "string") {
                    const jsonStartIndex = message.indexOf("{");
                    if (jsonStartIndex !== -1) {
                        const jsonString = message.slice(jsonStartIndex);
                        messageData = JSON.parse(jsonString);
                    } else {
                        throw new Error("No JSON found in message");
                    }
                } else {
                    messageData = message;
                }

                if (
                    messageData.type === "SIGNING_COMPLETED" &&
                    messageData.fileId === fileId &&
                    messageData.signerEmail !== currentUserEmail
                ) {
                    setNotificationMessage(
                        `${messageData.signerEmail} has completed signing the document.`
                    );
                    setShowRefreshModal(true);
                }

                if (
                    messageData.type === "CONFIGURATION_UPDATED" &&
                    messageData.fileId === fileId
                ) {
                    const configChangeMessage = messageData.changeType === 'MODIFIED'
                        ? `Document configuration has been updated by ${messageData.adminEmail}.`
                        : `Document configuration has been saved by ${messageData.adminEmail}.`;

                    setNotificationMessage(configChangeMessage);
                    setShowRefreshModal(true);
                }

            } catch (error) {
            }
        });

        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
    }, [fileId, token]);

    useEffect(() => {
        if (fileId && token) {
            loadDocumentData();
        }
    }, [fileId, token]);

    const refreshDocumentData = async () => {
        setShowRefreshModal(false);
        setIsLoading(true);
        try {
            const configResponse = await SecureDocsService.getFileConfigurations(fileId);
            if (configResponse?.data) {
                const freshPdfBase64 = configResponse.data.file;
                const freshElements = configResponse.data.configurations?.elements || configResponse.data.elements || [];

                const byteCharacters = atob(freshPdfBase64);
                const byteArray = Uint8Array.from(byteCharacters, (c) => c.charCodeAt(0));

                setPdfData(byteArray);
                setOriginalPdfBase64(freshPdfBase64);

                const fields: DocumentField[] = freshElements.map(element => ({
                    id: element.element_id,
                    type: element.element_type.toLowerCase(),
                    page: element.placeholder.page,
                    position: {
                        x: Number(element.placeholder.x),
                        y: Number(element.placeholder.y),
                        width: Number(element.placeholder.width),
                        height: Number(element.placeholder.height)
                    },
                    assignedTo: {
                        recipient_id: element.assigned_recipient?.recipient_id || element.assigned_recipient,
                        email: element.assigned_recipient?.email || "unknown@email.com"
                    },
                    completed: element.is_completed || false,
                    value: element.value || '',
                    completedAt: element.completed_at || null
                }));

                setDocumentFields(fields);

                const currentUserEmail = decodeJWT(token).UserId;
                const existingInputs = new Map<string, UserInput>();
                const currentUserInputs = new Map(userInputs);

                fields.forEach(field => {
                    const isCurrentUser = field.assignedTo.email === currentUserEmail;

                    if (isCurrentUser) {
                        if (currentUserInputs.has(field.id)) {
                            existingInputs.set(field.id, currentUserInputs.get(field.id)!);
                        } else if (field.value && field.completed) {
                            existingInputs.set(field.id, {
                                fieldId: field.id,
                                value: field.value,
                                type: field.type
                            });
                        }
                    } else {
                        if (field.value && field.completed) {
                            existingInputs.set(field.id, {
                                fieldId: field.id,
                                value: field.value,
                                type: field.type
                            });
                        }
                    }
                });

                setUserInputs(existingInputs);
                setNotificationMessage("Document updated! You can now see the latest changes.");
                setTimeout(() => setNotificationMessage(''), 3000);

            } else {
                setNotificationMessage("No updates available at this time.");
                setTimeout(() => setNotificationMessage(''), 3000);
            }

        } catch (error) {
            setError("Failed to refresh document data");
        } finally {
            setIsLoading(false);
        }
    };

    const notifySigningCompletion = () => {
        if (socketRef.current && fileId && token) {
            const currentUserEmail = decodeJWT(token)?.UserId;

            const notificationData = {
                type: 'SIGNING_COMPLETED',
                fileId: fileId,
                signerEmail: currentUserEmail,
                timestamp: Date.now(),
                message: `${currentUserEmail} has completed signing the document.`
            };

            socketRef.current.emit("chat message", JSON.stringify(notificationData));
        }
    };

    const loadDocumentData = async () => {
        try {
            setIsLoading(true);
            setError(null);

            if (!response) {
                throw new Error('No response received');
            }

            let cleanBase64 = pdfBase64.trim();
            if (cleanBase64.startsWith('data:')) {
                const base64Index = cleanBase64.indexOf('base64,');
                if (base64Index !== -1) {
                    cleanBase64 = cleanBase64.substring(base64Index + 7);
                }
            }
            const byteArray = base64ToUint8Array(cleanBase64);

            setPdfData(byteArray);
            setOriginalPdfBase64(cleanBase64);

            const fields: DocumentField[] = configurations.map((element: any) => ({
                id: element.element_id,
                type: element.element_type.toLowerCase(),
                page: element.placeholder.page,
                position: {
                    x: Number(element.placeholder.x),
                    y: Number(element.placeholder.y),
                    width: Number(element.placeholder.width),
                    height: Number(element.placeholder.height)
                },
                assignedTo: element.assigned_recipient,
                completed: element.is_completed,
                value: element.value || '',
                completedAt: element.completed_at
            }));

            setDocumentFields(fields);

            const currentUserEmail = decodeJWT(token).UserId;
            const existingInputs = new Map<string, UserInput>();

            fields.forEach(field => {
                if (field.value && field.completed) {
                    existingInputs.set(field.id, {
                        fieldId: field.id,
                        value: field.value,
                        type: field.type
                    });
                }
            });

            setUserInputs(existingInputs);

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to load document";
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (fieldId: string, value: string, type: string) => {
        if (type.toLowerCase() === 'signature' && value) {
            setMasterSignature(value);

            setUserInputs(prev => {
                const newInputs = new Map(prev);
                const currentUserEmail = decodeJWT(token).UserId;

                const signatureFields = documentFields.filter(
                    field => field.type.toLowerCase() === 'signature' &&
                        field.assignedTo.email === currentUserEmail
                );

                signatureFields.forEach(field => {
                    if (prev.has(field.id)) {
                        newInputs.set(field.id, { fieldId: field.id, value, type });
                    }
                });

                newInputs.set(fieldId, { fieldId, value, type });

                return newInputs;
            });
        } else {
            setUserInputs(prev => {
                const newInputs = new Map(prev);
                newInputs.set(fieldId, { fieldId, value, type });
                return newInputs;
            });
        }
    };

    const handleSignatureFieldClick = (fieldId: string) => {
        handleSignatureFieldOpen(fieldId);
    };

    const clearSavedSignature = () => {
        setMasterSignature(null);

        setUserInputs(prev => {
            const newInputs = new Map(prev);
            const currentUserEmail = decodeJWT(token).UserId;
            const signatureFields = documentFields.filter(
                field => field.type.toLowerCase() === 'signature' &&
                    field.assignedTo.email === currentUserEmail
            );

            signatureFields.forEach(field => {
                newInputs.delete(field.id);
            });

            return newInputs;
        });
    };

    const handleSignatureFieldOpen = (fieldId: string) => {
        setActiveSignatureFieldId(fieldId);
    };

    const handleSignatureFieldClose = () => {
        setActiveSignatureFieldId(null);
    };

    const generateCompletedPDF = async (): Promise<string> => {
        try {
            let workingPdfData = pdfData;
            if (!pdfData || !(pdfData instanceof Uint8Array) || pdfData.length === 0) {
                try {
                    workingPdfData = base64ToUint8Array(originalPdfBase64);
                } catch (restoreError) {
                }
            }

            if (userInputs.size === 0) {
                return originalPdfBase64 || uint8ArrayToBase64(workingPdfData);
            }

            let PDFDocument, rgb;
            try {
                const pdfLib = await import('pdf-lib');
                PDFDocument = pdfLib.PDFDocument;
                rgb = pdfLib.rgb;
            } catch (importError) {
                return originalPdfBase64 || uint8ArrayToBase64(workingPdfData);
            }

            let pdfDoc;
            try {
                const pdfDataCopy = new Uint8Array(workingPdfData);
                pdfDoc = await PDFDocument.load(pdfDataCopy);
            } catch (pdfLoadError) {
                try {
                    pdfDoc = await PDFDocument.load(workingPdfData, {
                        ignoreEncryption: true,
                        parseSpeed: 0
                    });
                } catch (altError) {
                    return originalPdfBase64 || uint8ArrayToBase64(workingPdfData);
                }
            }
            const pages = pdfDoc.getPages();
            if (pages.length === 0) {
                return originalPdfBase64 || uint8ArrayToBase64(workingPdfData);
            }

            const userInputsArray = Array.from(userInputs.entries());

            for (let i = 0; i < userInputsArray.length; i++) {
                const [fieldId, userInput] = userInputsArray[i];

                const field = documentFields.find(f => f.id === fieldId);
                if (!field) continue;

                const pageIndex = field.page - 1;
                if (pageIndex < 0 || pageIndex >= pages.length) continue;

                const page = pages[pageIndex];
                const actualPageSize = page.getSize();

                const BASE_WIDTH = 800;
                const scale = actualPageSize.width / BASE_WIDTH;
                const x = field.position.x * scale;
                const y = actualPageSize.height - (field.position.y * scale) - (field.position.height * scale);

                const fieldWidth = field.position.width * scale;
                const fieldHeight = field.position.height * scale;

                try {
                    const fontSize = Math.max(8, Math.min(16, fieldHeight * 0.6));

                    switch (field.type.toLowerCase()) {
                        case 'signature':
                            if (userInput.value && userInput.value.startsWith('data:image/')) {
                                try {
                                    let embeddedImage;

                                    if (userInput.value.includes('data:image/png')) {
                                        embeddedImage = await pdfDoc.embedPng(userInput.value);
                                    } else if (userInput.value.includes('data:image/jpeg') || userInput.value.includes('data:image/jpg')) {
                                        embeddedImage = await pdfDoc.embedJpg(userInput.value);
                                    } else {
                                        embeddedImage = await pdfDoc.embedPng(userInput.value);
                                    }

                                    page.drawImage(embeddedImage, {
                                        x: Math.max(0, x),
                                        y: Math.max(0, y),
                                        width: fieldWidth,
                                        height: fieldHeight,
                                    });
                                } catch (imageError) {
                                    page.drawText('[Signature]', {
                                        x: Math.max(5, x + 5),
                                        y: Math.max(5, y + fieldHeight / 2 - fontSize / 2),
                                        size: fontSize,
                                        color: rgb(0.5, 0.5, 0.5),
                                    });
                                }
                            } else if (userInput.value && userInput.value.trim() !== '') {
                                page.drawText(userInput.value, {
                                    x: Math.max(5, x + 5),
                                    y: Math.max(5, y + fieldHeight / 2 - fontSize / 2),
                                    size: Math.max(fontSize, 25),
                                    color: rgb(0, 0, 0),
                                });
                            }
                            break;

                        case 'initials':
                            if (userInput.value && userInput.value.trim() !== '') {
                                page.drawText(userInput.value, {
                                    x: Math.max(5, x + 5),
                                    y: Math.max(5, y + fieldHeight / 2 - Math.max(fontSize, 14) / 2),
                                    size: Math.max(fontSize, 14),
                                    color: rgb(0, 0, 0),
                                });
                            }
                            break;

                        case 'text':
                            if (userInput.value && userInput.value.trim() !== '') {
                                page.drawText(userInput.value, {
                                    x: Math.max(5, x + 5),
                                    y: Math.max(5, y + fieldHeight / 2 - fontSize / 2),
                                    size: fontSize,
                                    color: rgb(0, 0, 0),
                                });
                            }
                            break;

                        case 'checkbox':
                            if (userInput.value === 'true' || userInput.value === 'checked') {

                                // 🎯 Smaller balanced checkmark
                                const rawSize = Math.min(fieldWidth, fieldHeight) * 0.36;
                                const checkSize = Math.max(7, Math.min(rawSize, 14));

                                const centerX = x + fieldWidth / 2;
                                const centerY = y + fieldHeight / 2;

                                const leftX = centerX - checkSize * 0.40;
                                const leftY = centerY - checkSize * 0.05;

                                const midX = centerX - checkSize * 0.20;
                                const midY = centerY - checkSize * 0.38;

                                const rightX = centerX + checkSize * 0.44;
                                const rightY = centerY + checkSize * 0.32;

                                const stroke = Math.max(1.1, checkSize * 0.06);

                                page.drawLine({
                                    start: { x: leftX, y: leftY },
                                    end: { x: midX, y: midY },
                                    thickness: stroke,
                                    color: rgb(0, 0, 0),
                                });

                                page.drawLine({
                                    start: { x: midX, y: midY },
                                    end: { x: rightX, y: rightY },
                                    thickness: stroke,
                                    color: rgb(0, 0, 0),
                                });
                            }
                            break;

                        case 'date':
                        case 'datetime':
                            if (userInput.value && userInput.value.trim() !== '') {
                                page.drawText(userInput.value, {
                                    x: Math.max(5, x + 5),
                                    y: Math.max(5, y + fieldHeight / 2 - fontSize / 2),
                                    size: fontSize,
                                    color: rgb(0, 0, 0),
                                });
                            }
                            break;

                        default:
                    }
                } catch (fieldError) {
                }
            }

            let pdfBytes;
            try {
                pdfBytes = await pdfDoc.save();
            } catch (saveError) {
                return originalPdfBase64 || uint8ArrayToBase64(workingPdfData);
            }

            const pdfBase64 = uint8ArrayToBase64(pdfBytes);
            return pdfBase64;

        } catch (error) {
            if (originalPdfBase64) {
                return originalPdfBase64;
            } else if (pdfData && pdfData.length > 0) {
                return uint8ArrayToBase64(pdfData);
            }
        }
    };

    const submitSignedDocument = async () => {
        try {
            setIsSaving(true);
            setError(null);

            if (userFields.length === 0) return;
            if (completedUserFields.length === 0) return;

            let completedPdfBase64;
            try {
                completedPdfBase64 = await generateCompletedPDF();
            } catch (pdfGenerationError) {
                return;
            }

            const submissionData = {
                element_ids: Array.from(userInputs.keys()),
                timestamp: Math.floor(Date.now() / 1000),
                file: completedPdfBase64
            };

            try {
                const apiResponse = await SecureDocsService.markAsCompleted(fileId!, submissionData, token);
                if (apiResponse?.status === 200) {
                    notifySigningCompletion();
                    setToast(ToasterTexts.SECURE_DOC_SIGN_SUCCESS, ToasterType.SUCCESS);
                    setIsSaving(false);
                    navigate(`${AppRoutes.FALKON_DOCS_END_USER_SIGN_SUCCESS}`, {
                        state: {
                            filename: fileName,
                        },
                    });
                } else {
                    setToast(ToasterTexts.SECURE_DOC_MARK_AS_COMPLETED_RESPONSE, ToasterType.DANGER);
                }
            } catch (apiError) {
                throw apiError;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to submit signed document";
            setError(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    const goToNextPage = () => setCurrentPage(prev => Math.min(prev + 1, numPages));
    const goToFirstPage = () => setCurrentPage(1);
    const goToLastPage = () => setCurrentPage(numPages);

    const goToPreviousFieldPage = () => {
        const currentIndex = pagesWithUserFields.indexOf(currentPage);
        if (currentIndex > 0) {
            setCurrentPage(pagesWithUserFields[currentIndex - 1]);
        } else if (currentIndex === -1 && pagesWithUserFields.length > 0) {
            const previousPages = pagesWithUserFields.filter(p => p < currentPage);
            if (previousPages.length > 0) {
                setCurrentPage(previousPages[previousPages.length - 1]);
            }
        }
    };

    const goToNextFieldPage = () => {
        const currentIndex = pagesWithUserFields.indexOf(currentPage);
        if (currentIndex !== -1 && currentIndex < pagesWithUserFields.length - 1) {
            setCurrentPage(pagesWithUserFields[currentIndex + 1]);
        } else if (currentIndex === -1 && pagesWithUserFields.length > 0) {
            const nextPages = pagesWithUserFields.filter(p => p > currentPage);
            if (nextPages.length > 0) {
                setCurrentPage(nextPages[0]);
            }
        }
    };

    const canGoPreviousFieldPage = useMemo(() => {
        const currentIndex = pagesWithUserFields.indexOf(currentPage);
        if (currentIndex > 0) return true;
        if (currentIndex === -1) {
            return pagesWithUserFields.some(p => p < currentPage);
        }
        return false;
    }, [currentPage, pagesWithUserFields]);

    const canGoNextFieldPage = useMemo(() => {
        const currentIndex = pagesWithUserFields.indexOf(currentPage);
        if (currentIndex !== -1 && currentIndex < pagesWithUserFields.length - 1) return true;
        if (currentIndex === -1) {
            return pagesWithUserFields.some(p => p > currentPage);
        }
        return false;
    }, [currentPage, pagesWithUserFields]);

    const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const pageNum = parseInt(e.target.value);
        if (pageNum >= 1 && pageNum <= numPages) {
            setCurrentPage(pageNum);
        }
    };

    const handlePdfLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handlePageRenderSuccess = (page: any) => {
        if (page && page.originalWidth && page.originalHeight) {
            setActualPdfSize({
                width: page.originalWidth,
                height: page.originalHeight
            });
        }
    };

    const isSubmitDisabled = useMemo(() => {
        const allFieldsCompleted = completedUserFields.length === userFields.length;
        return isSaving || !allFieldsCompleted || !agreementAccepted;
    }, [isSaving, completedUserFields.length, userFields.length, agreementAccepted]);

    if (error) {
        return (
            <div className="p-relative h-v-100 d-flex justify-center align-center t-align-center">
                <div className="load-config-container">
                    <div className="error-message h4-semibold">
                        <div>Error Loading Document...</div>
                        <p>{error}</p>
                    </div>
                    <button onClick={loadDocumentData} className="retry-button h4-medium color-primary-accent">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!token) {
        return (
            <div className="d-flex align-center t-align-center h3-medium h-v-100">
                <div>Please log in to sign documents</div>
            </div>
        );
    }

    return (
        <div className={`d-flex flex-direction  ${IsMobileDevice ? "h-v-90" : "h-v-100"}`}>
            {showAgreementModal && (
                <div className="refresh-modal-overlay t-0 r-0 l-0 b-0 p-fixed d-flex align-center justify-center" style={{ zIndex: 1000 }}>
                    <div className="refresh-modal-box bg-color-primary-divider p-3">
                        <h3 className="color-primary-accent h3-semibold mb-3">User Agreement Confirmation</h3>

                        <div className="mb-4">
                            <div className="d-flex align-start mb-2">
                                <input
                                    type="checkbox"
                                    id="agreement-checkbox"
                                    checked={confirmationChecked}
                                    onChange={(e) => setConfirmationChecked(e.target.checked)}
                                    className="confirmation-checkbox mt-1"
                                    style={{ marginRight: '10px' }}
                                />
                                <label htmlFor="agreement-checkbox" className="confirmation-label c-pointer">
                                    <div className={`${IsMobileDevice ? 'h5-semibold' : 'h4-semibold'} color-primary mb-1`}>
                                        I agree to use electronic records and signatures. <span className="color-primary-danger">*</span>
                                    </div>
                                    <div className="h6-medium color-primary-inactive">
                                        By checking this box you consent to receive documents electronically and to sign them using an electronic signature. You can withdraw consent at any time.
                                    </div>
                                </label>
                            </div>
                        </div>

                        <ButtonComponent
                            OnClick={() => {
                                if (confirmationChecked) {
                                    setAgreementAccepted(true);
                                    setShowAgreementModal(false);
                                }
                            }}
                            ButtonLabel="Continue"
                            ClassName="mx-auto"
                            Variant={confirmationChecked ? "primary-btn" : "disabled-btn"}
                        />
                    </div>
                </div>
            )}

            {showRefreshModal && (
                <div className="refresh-modal-overlay t-0 r-0 l-0 b-0 p-fixed d-flex align-center justify-center">
                    <div className="refresh-modal-box bg-color-primary-divider">
                        <h3 className="color-primary-accent h4-semibold">{appNote.SECURE_DOC_END_USER_FILE_REFRESHED}</h3>
                        <p className="color-primary-accent-alt h5-medium">{notificationMessage}</p>
                        <p className="color-primary-accent-alt h6-medium">
                            {appNote.SECURE_DOC_END_USER_FILE_REFRESH_MODAL_TEXT}
                        </p>
                        <ButtonComponent
                            OnClick={refreshDocumentData}
                            ButtonLabel={buttonLabels.SECURE_DOC_REFRESH}
                            ClassName={"mx-auto"}
                            Variant={"primary-btn"}
                        />
                    </div>
                </div>
            )}
            <div className={`w-p-100  bg-color-end-user-doc-header ${IsMobileDevice ? "" : "h-p-10"}`}>
                <div className={`d-flex  w-p-95 m-auto  ${IsMobileDevice ? "flex-direction align-center py-1" : "justify-between align-center h-p-100"}`}>
                    <div className="doc-header-logo">
                        <FalconLogo />
                    </div>

                    <div className="flex-1 t-align-center">
                        <div className="m-0 color-primary-accent h4-semibold">Document Signing</div>
                        <div className="mt-1 h5-semibold primary-accent-alt">{decodeJWT(token).UserId}</div>
                    </div>

                    <div className="doc-header-right d-flex align-center gap-3">
                        {pagesWithUserFields.length > 0 && agreementAccepted && (
                            <div className={`field-navigation-container ${remainingFields > 0 ? 'bg-color-primary-light' : 'bg-color-secondary-success'}`}>
                                <button
                                    onClick={goToPreviousFieldPage}
                                    disabled={!canGoPreviousFieldPage}
                                    className={`nav-arrow-button ${remainingFields > 0 ? 'color-primary-light' : 'color-secondary-success'}`}
                                    title="Previous page with fields"
                                >
                                    <ChevronLeftIcon />
                                </button>

                                <div className="field-badge color-secondary h4-medium ">
                                    <span>
                                        {remainingFields > 0
                                            ? `${remainingFields} required field${remainingFields !== 1 ? 's' : ''} remaining`
                                            : 'No fields remaining'}
                                    </span>
                                </div>

                                <button
                                    onClick={goToNextFieldPage}
                                    disabled={!canGoNextFieldPage}
                                    className={`nav-arrow-button ${remainingFields > 0 ? 'color-primary-light' : 'color-secondary-success'}`}
                                    title="Next page with fields"
                                >
                                    <ChevronRightIcon />
                                </button>
                            </div>

                        )}
                    </div>
                </div>
            </div>
            <div className="pdf-viewer-container h-p-70" ref={pdfContainerRef}>
                {!activeSignatureFieldId && (
                    <div className="pdf-navigation d-flex align-center justify-center bg-color-secondary h6-medium" style={{
                        top: IsMobileDevice ? '0' : '90px',
                    }}>
                        <button onClick={goToFirstPage} disabled={currentPage === 1} className="pdf-nav-button b-none h6-medium c-pointer">⏮️ First</button>
                        <button onClick={goToPreviousPage} disabled={currentPage === 1} className="pdf-nav-button b-none h6-medium c-pointer">⬅️ Previous</button>

                        <div className="pdf-page-input-wrapper d-flex align-center">
                            <span className="h6-medium color-primary-dark">Page</span>
                            <input
                                type="number"
                                className="pdf-page-input h6-medium t-align-center color-primary-dark"
                                value={currentPage}
                                onChange={handlePageInputChange}
                                min={1}
                                max={numPages}
                            />
                            <span className="h6-medium color-primary-dark">of {numPages}</span>
                        </div>

                        <button onClick={goToNextPage} disabled={currentPage === numPages} className="pdf-nav-button b-none h6-medium c-pointer">Next ➡️</button>
                        <button onClick={goToLastPage} disabled={currentPage === numPages} className="pdf-nav-button b-none h6-medium c-pointer">Last ⏭️</button>
                    </div>
                )}
                {pdfData ? (
                    <div className="h-p-85 o-y-scroll">
                        <div className="pdf-document-wrapper d-flex justify-center p-relative">
                            <Document
                                file={fileData}
                                onLoadSuccess={handlePdfLoadSuccess}
                                onLoadError={(error) => {
                                    setError('Failed to load PDF document');
                                }}
                            >
                                <Page
                                    pageNumber={currentPage}
                                    width={pdfWidth}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                    onRenderSuccess={handlePageRenderSuccess}
                                />
                            </Document>

                            {documentFields
                                .filter(field => field.page === currentPage)
                                .map((field) => (
                                    <InteractiveSigningField
                                        key={field.id}
                                        field={field}
                                        isUserField={field.assignedTo.email === decodeJWT(token).UserId}
                                        userInput={userInputs.get(field.id)}
                                        onFieldUpdate={handleFieldUpdate}
                                        currentPage={currentPage}
                                        onSignatureFieldOpen={() => handleSignatureFieldClick(field.id)}
                                        onSignatureFieldClose={handleSignatureFieldClose}
                                        isHidden={activeSignatureFieldId !== null && activeSignatureFieldId !== field.id}
                                        pdfWidth={pdfWidth}
                                        savedSignature={masterSignature}
                                        onClearSavedSignature={clearSavedSignature}
                                    />
                                ))}
                        </div>
                    </div>
                ) : (
                    <p className="h3-semibold t-align-center mt-2 color-primary-accent-alt">No PDF found</p>
                )}
            </div>

            <div className="bg-color-end-user-doc-header d-flex justify-center align-center t-align-center h-p-10">
                <ButtonComponent
                    OnClick={submitSignedDocument}
                    ButtonLabel={isSaving ? buttonLabels.SECURE_DOC_SIGN_SUBMITTING : buttonLabels.SECURE_DOC_SIGN_SUBMITTED}
                    ClassName="send-button mt-0"
                    Variant={isSubmitDisabled ? "disabled-btn-small" : "primary-btn-small"}
                />
            </div>
        </div>
    );
};

export default EndUserSigningComponent;